import cx from 'classnames';
import dayjs from 'dayjs';
import React from 'react';
import { IJobDetail } from 'types/jobs';

import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';

import styles from './JobInformation.module.scss';
import Building from './icons/building.svg';
import Calendar from './icons/calendar.svg';
import Local from './icons/local.svg';
import Money from './icons/money.svg';

export enum JobInfoStyle {
  Vertical = 'Vertical',
  Horizontal = 'Horizontal',
}

export interface JobInformationProps {
  job: IJobDetail;
  style?: JobInfoStyle;
}

const dateFormat = (date: string): string | null => {
  return date ? dayjs(date).format('DD MMM YYYY') : /* istanbul ignore next */ null;
};

export const getJobDates = (workType: string, startDate?: string, endDate?: string): string =>
  workType === 'Permanent' ? showPermanentDate(startDate) : showLocumDate(startDate, endDate);

const showPermanentDate = (startDate?: string): string => {
  if (!startDate || new Date(startDate).valueOf() < new Date().valueOf()) {
    return 'Start dates are flexible';
  }
  return `Available from ${dateFormat(startDate)}`;
};

const showLocumDate = (startDate?: string, endDate?: string): string => {
  const endD = endDate ? ` ~ ${dateFormat(endDate)}` : '';
  if (!startDate || new Date(startDate).valueOf() < new Date().valueOf()) {
    return `NOW${endD}`;
  }
  return `${dateFormat(startDate)}${endD}`;
};
export const salaryFormat = (num: number): string | null => {
  /* istanbul ignore if */
  if (!num) {
    return null;
  }
  const str = num.toString();
  const reg = str.indexOf('.') > -1 ? /(\d)(?=(\d{3})+\.)/g : /(\d)(?=(?:\d{3})+$)/g;
  return str.replace(reg, '$1,');
};
const isValidValue = (payValue: number) => payValue && payValue > 0;
export const showPay = (
  workType: string,
  payRate?: number,
  payInterval?: string,
  salaryFrom?: number,
  salaryTo?: number,
  ofBillings?: number,
  guaranteedHourlyRate?: number,
): string => {
  if (
    ofBillings &&
    isValidValue(ofBillings) &&
    guaranteedHourlyRate &&
    isValidValue(guaranteedHourlyRate)
  ) {
    return `${ofBillings}% of billings or $${guaranteedHourlyRate} Guaranteed Hourly Rate`;
  }

  if (ofBillings && isValidValue(ofBillings)) {
    return `${ofBillings}% of billings`;
  }

  if (guaranteedHourlyRate && isValidValue(guaranteedHourlyRate)) {
    return `$${guaranteedHourlyRate} Guaranteed Hourly Rate`;
  }

  if (workType === 'Locum' && payRate && isValidValue(payRate)) {
    return `$${salaryFormat(payRate) ?? ''} ${payInterval ?? ''}`;
  }

  if (
    workType === 'Permanent' &&
    salaryFrom &&
    isValidValue(salaryFrom) &&
    salaryTo &&
    isValidValue(salaryTo)
  ) {
    return `$${salaryFormat(salaryFrom) ?? ''} ~ $${salaryFormat(salaryTo) ?? ''}`;
  }

  return 'Available on application';
};

export const showLocation = (location: { state?: string; city?: string; country?: string }) => {
  return (
    <>
      {location.city && <span data-testid="job-location-city">{location.city}, </span>}
      <span data-testid="job-location-state">{location.state}</span>
      <span data-testid="job-location-country"> {location.country}</span>
    </>
  );
};

type Information = {
  icon: React.ReactNode;
  content: React.ReactNode;
  tag: string;
  label: string;
};

export const JobInformation = ({ job, style }: JobInformationProps) => {
  const {
    workType,
    city,
    country,
    locationDisplay,
    payRate,
    payInterval,
    endDate,
    startDate,
    salaryFrom,
    salaryTo,
    ofBillings,
    guaranteedHourlyRate,
  } = job;

  const information: Information[] = [
    {
      icon: <Local width={15} />,
      content: showLocation({
        city: city,
        state: locationDisplay,
        country: country,
      }),
      tag: 'location',
      label: 'Location',
    },
    {
      icon: <Building width={15} />,
      content: workType,
      tag: 'workType',
      label: 'Work Type',
    },
    {
      icon: <Money width={18} />,
      content: showPay(
        workType,
        payRate,
        payInterval,
        salaryFrom,
        salaryTo,
        ofBillings,
        guaranteedHourlyRate,
      ),
      tag: 'salary',
      label: 'Salary',
    },
    {
      icon: <Calendar width={18} />,
      content: getJobDates(workType, startDate, endDate),
      tag: 'date',
      label: 'Date',
    },
  ];

  return (
    <List className={cx(styles.container, style && styles[style])} data-testid={'job-info'}>
      {information.map(item => (
        <ListItem key={item.tag} className={cx(styles.listItem, styles[item.tag])}>
          <ListItemIcon>
            <span className={styles.icon} aria-hidden="true">
              {item.icon}
            </span>
          </ListItemIcon>
          <ListItemText aria-label={item.label} data-testid={item.label}>
            {item.content}
          </ListItemText>
        </ListItem>
      ))}
    </List>
  );
};
